/* nella-overlay-history.js
 * Single-state overlay back-gesture coordinator.
 * Invariant: at most one overlay owns the active history entry at a time, and at
 * most one synthetic history entry ever exists. When a second overlay opens while
 * one is already active, the existing slot is TRANSFERRED (no new pushState, no
 * history.back) rather than abandoned. Abandoning left stacked same-URL entries,
 * which exposed the white WKWebView root layer on the iOS interactive back gesture.
 *
 * Boost interaction: overlays push a SAME-URL history entry. When the overlay closes
 * (button or back/swipe), a popstate fires. Boost's onPopState re-renders the
 * collection grid on any non-hash popstate, which made B2B prices re-skeleton on every
 * overlay open/close. isOverlayPopstate() lets Boost detect these synthetic same-URL
 * pops and skip its history re-render, while real filter/sort/pagination back/forward
 * (which change the URL) still re-render normally.
 *
 * Usage in each overlay:
 *   open:                 NellaOverlayHistory.push(this)
 *   manual/button close:  NellaOverlayHistory.pop(false)
 *   competing-UI close:   NellaOverlayHistory.pop(true)     — no history.back()
 *   replace (reuse slot): NellaOverlayHistory.transfer(this)
 *   bfcache/immediate:    NellaOverlayHistory.release(this)
 *   implement:            closeFromBackGesture() — called by this helper on popstate
 */
(function () {
  'use strict';

  var _controller = null;
  var _pushed = false;
  var _cleanup = false;
  var _ignoreNextPop = false;
  // URL the page was at when the active overlay opened (the overlay pushes a same-URL
  // entry, so this is also the URL the page returns to when the overlay closes). Boost's
  // onPopState compares against it to skip re-rendering on overlay synthetic pops.
  var _overlayBaseUrl = null;

  function canUse() {
    return !!(
      window.history &&
      typeof window.history.pushState === 'function' &&
      typeof window.history.back === 'function'
    );
  }

  // Register this controller as the active overlay and push the single history entry.
  // No-op if something is already pushed (only one synthetic entry at a time).
  function push(controller) {
    if (!canUse() || _pushed) return;
    _controller = controller;
    _pushed = true;
    _cleanup = false;
    _ignoreNextPop = false;
    _overlayBaseUrl = window.location.href;
    history.pushState({ nellaOverlayOpen: true }, '');
  }

  // Hand the existing active slot to a different overlay WITHOUT touching browser
  // history. Used when one overlay replaces another (e.g. Search open -> Menu open).
  function transfer(toController) {
    if (!canUse() || !_pushed) return false;
    _controller = toController;
    _ignoreNextPop = true;
    return true;
  }

  // Called by the overlay's close() method.
  function pop(skipHistoryCleanup) {
    if (!canUse() || !_pushed) return;
    if (_ignoreNextPop) {
      _ignoreNextPop = false;
      return; // slot transferred to the incoming overlay — keep _overlayBaseUrl
    }
    _pushed = false;
    _controller = null;
    if (skipHistoryCleanup) {
      // Competing-UI abandon: no history.back(), so no popstate will clear the base URL.
      scheduleBaseUrlClear();
      return;
    }
    _cleanup = true;
    history.back();
  }

  // Called on bfcache freeze / immediate close — abandons state without history.back().
  function release(controller) {
    if (_controller === controller) {
      _pushed = false;
      _controller = null;
      _cleanup = false;
      _ignoreNextPop = false;
      _overlayBaseUrl = null;
    }
  }

  // Called by Boost's onPopState (boost-pfs-core.js) to decide whether a popstate is an
  // app-overlay synthetic close/back rather than a real collection navigation. True when
  // an overlay opened (pushed a same-URL entry) and the current URL still equals that
  // pre-overlay URL — i.e. the filter/query/path did not change, so Boost must NOT
  // re-render the grid. A real Boost back/forward changes the URL, so this returns false.
  function isOverlayPopstate() {
    return _overlayBaseUrl !== null && window.location.href === _overlayBaseUrl;
  }

  // Clear the recorded base URL AFTER the current popstate's synchronous handlers (Boost
  // + ours) have all run, so the comparison stays valid during this event regardless of
  // listener order, but can never leak into a later real navigation that happens to
  // return to the same URL.
  function scheduleBaseUrlClear() {
    window.setTimeout(function () { _overlayBaseUrl = null; }, 0);
  }

  window.addEventListener('popstate', function () {
    // Absorb the popstate fired by our own history.back() in pop() (button/X/overlay
    // close). Overlay synthetic pop — keep the base URL valid for Boost this frame, then
    // clear it next tick.
    if (_cleanup) {
      _cleanup = false;
      scheduleBaseUrlClear();
      return;
    }
    // A real popstate consumes the slot; clear the one-shot transfer guard.
    _ignoreNextPop = false;
    if (!_pushed || !_controller) return;
    var c = _controller;
    _pushed = false;
    _controller = null;
    // Native back/swipe close: also an overlay synthetic pop returning to the base URL.
    scheduleBaseUrlClear();
    if (typeof c.closeFromBackGesture === 'function') {
      c.closeFromBackGesture();
    }
  });

  window.NellaOverlayHistory = {
    push: push,
    pop: pop,
    transfer: transfer,
    release: release,
    isOverlayPopstate: isOverlayPopstate
  };
})();
